/**
 * @file WmsWriteoffEditPage.tsx
 * @description Карточка списания (phase13.0; фасовки и подтверждение резервов —
 * phase19.13 этап 0; вид документа как у приёмки — phase19.23, A2; единый
 * шаблон документа — phase19.24 этап 1, шаг 1).
 *
 * Проведённый документ отвечает на вопросы «кто, когда, сколько и что это
 * сделало с остатком»: плитки meta, комментарий, позиции с партией и ячейкой,
 * блок «Движения по документу».
 *
 * Проведение живёт в `useWriteoffCommit`: сервер отвечает 409
 * `writeoff_reserved_stock`, если списание уводит остаток ниже активных
 * резервов, и мы повторяем commit с подтверждением кладовщика.
 *
 * phase19.31 (зона «Склад», минимум по SC50): шапка — в верхней панели
 * («назад», номер, статус, «⋯», «Провести списание»); своя липкая полоса
 * убрана — на телефоне действие в общей нижней полосе каркаса.
 */

import { Loader2 } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router";

import { Alert } from "../../../components/ui/alert";
import { DocumentLayout } from "../../../components/ui/DocumentLayout";
import { SectionLabel } from "../../../components/ui/SectionLabel";
import { WmsConfirmDialog } from "../../../components/wms/WmsConfirmDialog";
import { WmsDocumentMovements } from "../../../components/wms/WmsDocumentMovements";
import { WriteoffDocHeader } from "../../../components/wms/WriteoffDocHeader";
import { WriteoffDraftForm } from "../../../components/wms/WriteoffDraftForm";
import { WriteoffItemForm } from "../../../components/wms/WriteoffItemForm";
import { WriteoffItemsList } from "../../../components/wms/WriteoffItemsList";
import { WriteoffNotices } from "../../../components/wms/WriteoffNotices";
import { WriteoffReservedDialog } from "../../../components/wms/WriteoffReservedDialog";
import { useDocumentRowHighlight } from "../../../components/wms/useDocumentRowHighlight";
import { useWriteoffCommit } from "../../../components/wms/useWriteoffCommit";
import { useWriteoffDocument } from "../../../components/wms/useWriteoffDocument";

export function WmsWriteoffEditPage() {
  const { id, slug } = useParams<{ id: string; slug: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const isNew = id === "new" || !id;
  const presetLotId = search.get("lotId");
  // «Подбор» → фасовка уже в документе → прокрутка к её строке и подсветка.
  const highlight = useDocumentRowHighlight("writeoff-item");

  const doc = useWriteoffDocument(id, isNew);
  const { writeoff } = doc;
  // После проведения перечитываем документ целиком: ответ commit'а приходит
  // без позиций и автора, и шапка «Создал / Позиции» оставалась пустой до F5.
  const { commit, committing, conflicts, dismissConflicts, confirmRequest, settleConfirm } = useWriteoffCommit(
    writeoff?.id,
    doc.reload,
  );

  if (doc.loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="size-5 animate-spin text-primary" />
      </div>
    );
  }

  if (isNew) {
    return (
      <WriteoffDraftForm
        backTo={`/shop/${slug}/wms/writeoffs`}
        onCreated={(created) => navigate(`/shop/${slug}/wms/writeoffs/${created.id}`)}
      />
    );
  }

  if (doc.error || !writeoff) {
    return <Alert severity="error">{doc.error ?? "Списание не найдено"}</Alert>;
  }

  const isDraft = writeoff.status === "DRAFT";
  const items = writeoff.items ?? [];
  const addedSkuIds = items.map((it) => it.skuId);
  const goToSku = (skuId: string) => {
    const row = items.find((it) => it.skuId === skuId);
    if (row) highlight.goTo(row.id);
  };

  return (
    <DocumentLayout
      header={
        <WriteoffDocHeader
          writeoff={writeoff}
          backTo={`/shop/${slug}/wms/writeoffs`}
          committing={committing}
          onCommit={() => void commit(false)}
        />
      }
      notices={<WriteoffNotices notes={writeoff.notes} isDraft={isDraft} />}
      main={
        <section className="flex min-w-0 flex-col gap-2">
          <SectionLabel as="h2" count={items.length}>
            Позиции
          </SectionLabel>
          <WriteoffItemsList
            items={items}
            highlightedId={highlight.highlightedId}
            rowDomId={highlight.rowDomId}
            editable={isDraft}
            onRemove={(itemId) => void doc.removeItem(itemId)}
          />
        </section>
      }
      aside={
        isDraft ? (
          <WriteoffItemForm
            submitting={doc.adding}
            presetLotId={presetLotId}
            alreadyAddedIds={addedSkuIds}
            onGoToExisting={goToSku}
            onSubmit={doc.addItem}
          />
        ) : undefined
      }
      footer={
        !isDraft ? (
          <WmsDocumentMovements
            documentId={writeoff.id}
            documentKind="writeoff"
            warehouseId={writeoff.warehouseId}
          />
        ) : null
      }
      dialogs={
        <>
          {/* «Провести списание?» — окно кабинета вместо системного confirm(). */}
          <WmsConfirmDialog request={confirmRequest} onSettle={settleConfirm} />
          <WriteoffReservedDialog
            open={conflicts.length > 0}
            conflicts={conflicts}
            submitting={committing}
            onCancel={dismissConflicts}
            onConfirm={() => void commit(true)}
          />
        </>
      }
    />
  );
}
